import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Shield, CheckCircle, Clock, AlertTriangle, RefreshCw,
  User, Calendar, Phone, MapPin, Hash, ChevronDown, ChevronUp, Info
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending_schedule:      { color: 'bg-gray-100 text-gray-600 border-gray-200', icon: Clock, label: 'Awaiting Schedule' },
  partner_assigned:      { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: User, label: 'Partner Assigned' },
  no_partner_available:  { color: 'bg-orange-100 text-orange-700 border-orange-200', icon: RefreshCw, label: 'Finding Partner…' },
  needs_seller_reassignment: { color: 'bg-red-100 text-red-700 border-red-200', icon: AlertTriangle, label: 'Action Required' },
  verified:              { color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle, label: 'Verified ✓' },
};

// ─── Reassign form ────────────────────────────────────────────────────────────
const ReassignForm = ({ vehicleId, vehicleCity, sellerPhone, onSuccess, onCancel }) => {
  const { token } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [slots, setSlots] = useState([
    { date: '', from_time: '10:00', to_time: '18:00' },
    { date: '', from_time: '10:00', to_time: '18:00' },
  ]);
  const [address, setAddress] = useState({ street: '', city: vehicleCity || '', state: '', pin: '' });
  const [phone, setPhone] = useState(sellerPhone || '');

  const todayStr = () => new Date().toISOString().split('T')[0];

  const handleSubmit = async () => {
    const validSlots = slots.filter(s => s.date);
    if (!validSlots.length) { toast.error('Add at least one date slot'); return; }
    if (!address.street.trim()) { toast.error('Enter street address'); return; }
    if (address.pin.length < 6) { toast.error('Enter valid 6-digit PIN'); return; }
    if (phone.replace(/\D/g, '').length !== 10) { toast.error('Enter valid 10-digit phone'); return; }

    setSubmitting(true);
    try {
      await axios.post(`${API}/seller/reassign-verification-availability`, {
        vehicle_id: vehicleId,
        availability_slots: validSlots,
        address_street: address.street,
        address_city: address.city,
        address_state: address.state,
        address_pin: address.pin,
        seller_phone: phone,
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success('New availability submitted! System will find a partner.');
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit');
    } finally { setSubmitting(false); }
  };

  return (
    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
      className="mt-4 border-2 border-red-200 bg-red-50 rounded-xl p-5 space-y-4">
      <p className="text-sm font-bold text-red-800 flex items-center gap-2">
        <AlertTriangle className="h-4 w-4" /> Update Your Availability
      </p>
      <p className="text-xs text-red-600">
        We couldn't find a partner for your previous time slots. Please provide new availability windows so we can try again.
      </p>

      {slots.map((s, idx) => (
        <div key={idx} className="bg-white p-4 rounded-xl border border-red-200 space-y-3">
          <p className="text-xs font-bold text-gray-700">Slot {idx + 1}</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] text-gray-500 font-medium block mb-1">Date *</label>
              <input type="date" min={todayStr()} value={s.date}
                onChange={e => setSlots(prev => prev.map((x, i) => i === idx ? { ...x, date: e.target.value } : x))}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-red-400" />
            </div>
            <div>
              <label className="text-[10px] text-gray-500 font-medium block mb-1">From</label>
              <input type="time" min="10:00" max="16:30" value={s.from_time}
                onChange={e => setSlots(prev => prev.map((x, i) => i === idx ? { ...x, from_time: e.target.value } : x))}
                className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-[10px] text-gray-500 font-medium block mb-1">To</label>
              <input type="time" min="11:30" max="18:00" value={s.to_time}
                onChange={e => setSlots(prev => prev.map((x, i) => i === idx ? { ...x, to_time: e.target.value } : x))}
                className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
        </div>
      ))}

      <div className="bg-white p-4 rounded-xl border border-red-200 space-y-3">
        <p className="text-xs font-bold text-gray-700">Vehicle Location</p>
        <div className="space-y-2">
          <input placeholder="Street address *" value={address.street}
            onChange={e => setAddress(p => ({ ...p, street: e.target.value }))}
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-red-400" />
          <div className="grid grid-cols-3 gap-2">
            <input placeholder="City" value={address.city}
              onChange={e => setAddress(p => ({ ...p, city: e.target.value }))}
              className="border rounded-lg px-3 py-2 text-sm" />
            <input placeholder="State" value={address.state}
              onChange={e => setAddress(p => ({ ...p, state: e.target.value }))}
              className="border rounded-lg px-3 py-2 text-sm" />
            <input placeholder="PIN *" maxLength={6} value={address.pin}
              onChange={e => setAddress(p => ({ ...p, pin: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
              className="border rounded-lg px-3 py-2 text-sm" />
          </div>
        </div>
        <div>
          <label className="text-[10px] text-gray-500 font-medium block mb-1">Contact Phone *</label>
          <div className="flex">
            <span className="px-3 flex items-center bg-gray-100 border rounded-l-lg text-sm text-gray-500">+91</span>
            <input value={phone} maxLength={10}
              onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
              className="flex-1 border border-l-0 rounded-r-lg px-3 py-2 text-sm" />
          </div>
        </div>
      </div>

      <div className="flex gap-3">
        <Button onClick={onCancel} variant="outline" className="flex-1 h-9 text-sm">Cancel</Button>
        <Button onClick={handleSubmit} disabled={submitting}
          className="flex-1 h-9 bg-red-600 hover:bg-red-700 text-white text-sm">
          {submitting ? 'Submitting…' : 'Submit New Availability'}
        </Button>
      </div>
    </motion.div>
  );
};

// ─── Main widget ──────────────────────────────────────────────────────────────
const VerificationStatusWidget = ({ vehicleId, vehicleCity, sellerPhone }) => {
  const { token } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReassign, setShowReassign] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const POLL_INTERVAL = 30000; // 30 seconds

  const fetchStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/seller/verification-live-status/${vehicleId}`,
        { headers: { Authorization: `Bearer ${token}` } });
      setStatus(r.data);
    } catch {} finally { setLoading(false); }
  }, [vehicleId, token]);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  if (loading) return (
    <div className="flex items-center gap-2 py-3 text-gray-400 text-sm">
      <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-blue-500 rounded-full" />
      Loading verification status…
    </div>
  );

  if (!status) return null;

  const cfg = STATUS_CONFIG[status.verification_status] || STATUS_CONFIG.pending_schedule;
  const StatusIcon = cfg.icon;
  const isRetrying = status.schedule_status === 'no_partner_available';
  const needsAction = status.needs_reassignment;

  return (
    <div className={`rounded-xl border-2 overflow-hidden ${needsAction ? 'border-red-300' : isRetrying ? 'border-orange-300' : 'border-blue-200'}`}>
      {/* Header bar */}
      <div className={`px-4 py-3 flex items-center justify-between gap-3 ${needsAction ? 'bg-red-50' : isRetrying ? 'bg-orange-50' : 'bg-blue-50'}`}>
        <div className="flex items-center gap-2.5">
          <Shield className={`h-5 w-5 flex-shrink-0 ${needsAction ? 'text-red-500' : isRetrying ? 'text-orange-500' : 'text-blue-600'}`} />
          <div>
            <p className="text-sm font-bold text-gray-900">Verification Status</p>
            <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-full border ${cfg.color}`}>
              {cfg.label}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isRetrying && <RefreshCw className="h-4 w-4 text-orange-400 animate-spin" />}
          <button onClick={fetchStatus} className="text-xs text-gray-400 hover:text-gray-600 transition">
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="px-4 py-4 space-y-3 bg-white">

        {/* Notification message */}
        {status.seller_notification && (
          <div className={`p-3 rounded-xl text-xs border flex items-start gap-2 ${needsAction ? 'bg-red-50 border-red-200 text-red-700' : isRetrying ? 'bg-orange-50 border-orange-200 text-orange-700' : 'bg-blue-50 border-blue-200 text-blue-700'}`}>
            <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <p>{status.seller_notification}</p>
          </div>
        )}

        {/* Retry count */}
        {status.retry_count > 0 && (
          <p className="text-xs text-gray-400 flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" />
            System has made <b>{status.retry_count}</b> assignment attempt{status.retry_count > 1 ? 's' : ''} so far. Auto-retrying every 2 minutes.
          </p>
        )}

        {/* Partner info (if assigned) */}
        {status.assignment && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-xl space-y-1.5">
            <p className="text-xs font-bold text-green-800">Partner Details</p>
            <div className="grid grid-cols-2 gap-2 text-xs text-green-700">
              <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" />{status.assignment.partner_name || '—'}</span>
              <span className="flex items-center gap-1"><Hash className="h-3.5 w-3.5" />{status.assigned_partner_uid || '—'}</span>
              <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{status.assignment.scheduled_date || '—'}</span>
              <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{status.assignment.scheduled_from}–{status.assignment.scheduled_to}</span>
            </div>
          </div>
        )}

        {/* Verified badge */}
        {status.verified_by_x7autoz && (
          <div className="flex items-center gap-2 p-3 bg-green-100 border border-green-300 rounded-xl">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <div>
              <p className="text-sm font-bold text-green-800">Vehicle Verified!</p>
              <p className="text-xs text-green-600">Your vehicle has been verified by X7 Autoz. The badge is now showing on your listing.</p>
            </div>
          </div>
        )}

        {/* Reassign button */}
        {needsAction && !showReassign && (
          <Button onClick={() => setShowReassign(true)}
            className="w-full bg-red-600 hover:bg-red-700 text-white text-sm h-10 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Update My Availability
          </Button>
        )}

        {/* Reassign form */}
        <AnimatePresence>
          {showReassign && (
            <ReassignForm
              vehicleId={vehicleId}
              vehicleCity={vehicleCity}
              sellerPhone={sellerPhone}
              onSuccess={() => { setShowReassign(false); fetchStatus(); }}
              onCancel={() => setShowReassign(false)}
            />
          )}
        </AnimatePresence>

        {/* Auto-refresh note */}
        <p className="text-[10px] text-gray-400 text-right">Auto-refreshes every 30s</p>
      </div>
    </div>
  );
};

export default VerificationStatusWidget;